import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import userPlaceholder from '../../assets/user.png';
import GuestHistoricVoyageItem from '../../components/GuestHistoricVoyageItem';
import GuestNavlist from '../../components/GuestNavList';
import { useAxios } from '../../utils/hook/useAxios';
import { useUser } from '../../utils/hook/UserContext';
import '../mc-guest.css';

const STATUS_TABS = [
  { key: 'all', label: 'Toutes' },
  { key: 'pending', label: 'En attente' },
  { key: 'accepted', label: 'Acceptées' },
  { key: 'paid', label: 'Confirmées' },
  { key: 'completed', label: 'Terminées' },
  { key: 'rejected', label: 'Refusées' },
  { key: 'cancelled', label: 'Annulées' },
];

function GuestReservationPage() {
  const [ReservationData, setReservationData] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [isLoading, setIsLoading] = useState(true);

  const { user } = useUser();
  const axios = useAxios();
  const inviteId = user?.id;

  // Évite de recréer les réservations en double si `user` change de référence
  const bookingSubmitted = useRef(false);

  /* ── Créer la réservation si l'utilisateur vient de la page de booking ── */
  useEffect(() => {
    localStorage.setItem('mode', 'guest');
    const selectedCars = JSON.parse(localStorage.getItem('selectedVoitures')) || [];
    if (!user || selectedCars.length === 0) return;
    if (bookingSubmitted.current) return;
    bookingSubmitted.current = true;

    selectedCars.forEach((car) => {
      const personnalisedMessage = localStorage.getItem('personnalisedMessage');
      axios
        .post('/reservations', {
          driverHoteId: car.driverId,
          driverInviteId: user.id,
          vehiculeId: car.id_de_la_voiture,
          startDate: car.dateEntree,
          endDate: car.dateSortie,
          vehiculeAnnonceId: car.vehiculeAnnonceId,
          PaymentIntentId: null,
        })
        .then((response) =>
          axios
            .post('/conversations', {
              authorDriverRole: 1,
              driversId: car.driverId,
              sentTime: new Date(),
              reservationId: response.data.reservationId,
              message: personnalisedMessage,
            })
            .then((res) =>
              axios
                .post('/reservationsGain', {
                  reservationId: res.data.reservationId,
                  amount: car.prix_par_jour * car.nombre_de_jours,
                  type: '0',
                })
                .then(() => localStorage.removeItem('selectedVoitures'))
                .catch(console.error)
            )
            .catch(console.error)
        )
        .catch((err) => {
          if (err?.response?.status === 422)
            toast.error(
              err.response.data?.error || "Demande refusée par les préférences de l'hôte.",
              { autoClose: 6000 }
            );
          else console.error(err);
        });
    });
  }, [user]);

  /* ── Charger les réservations de l'invité ── */
  useEffect(() => {
    if (!inviteId) return;
    setIsLoading(true);
    axios
      .get('/reservations/getbyInviteid/' + inviteId)
      .then(({ data }) => {
        if (Array.isArray(data))
          setReservationData(
            [...data].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          );
      })
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, [inviteId]);

  const missingDocs = Boolean(user) && (!user?.PermisConduireDoc || !user?.PieceIdentityDoc);

  const filtered =
    activeTab === 'all' ? ReservationData : ReservationData.filter((r) => r.status === activeTab);
  const countByTab = (key) =>
    key === 'all' ? ReservationData.length : ReservationData.filter((r) => r.status === key).length;

  const initials = user
    ? `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase()
    : '';

  const stats = [
    { label: 'Total', value: ReservationData.length },
    {
      label: 'En cours',
      value: ReservationData.filter((r) => ['pending', 'accepted', 'paid'].includes(r.status))
        .length,
    },
    {
      label: 'Terminés',
      value: ReservationData.filter((r) => r.status === 'completed').length,
    },
  ];

  return (
    <main className="mc-home mc-guest-page">
      <ToastContainer />

      <div className="container mc-container">
        <div className="mc-guest-layout">
          {/* ════ Sidebar — desktop seulement ════ */}
          <aside className="mc-guest-aside" aria-label="Espace invité">
            {/* Profil */}
            <div className="mc-guest-card">
              <div className="mc-guest-profile">
                <div className="mc-guest-avatar">
                  {user?.profile_url ? (
                    <img src={user.profile_url} alt="" />
                  ) : initials ? (
                    initials
                  ) : (
                    <img src={userPlaceholder} alt="" />
                  )}
                </div>
                <div style={{ minWidth: 0 }}>
                  <p className="mc-guest-name">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="mc-guest-role">Invité Moovicar</p>
                </div>
              </div>

              {/* Compteurs */}
              <div className="mc-guest-stats">
                {stats.map(({ label, value }) => (
                  <div key={label} className="mc-guest-stat">
                    <strong>{value}</strong>
                    <span>{label}</span>
                  </div>
                ))}
              </div>

              {missingDocs && (
                <div className="mc-guest-alert" role="alert">
                  <i className="bi bi-exclamation-triangle-fill" aria-hidden="true"></i>
                  <span>
                    Documents manquants. <Link to="/guest/settings">Compléter →</Link>
                  </span>
                </div>
              )}

              <hr className="mc-guest-divider" />
              <GuestNavlist />
            </div>

            {/* Encart recherche */}
            <div className="mc-guest-promo">
              <span className="mc-guest-promo-icon" aria-hidden="true">
                <i className="bi bi-search"></i>
              </span>
              <h2>Trouver une voiture</h2>
              <p>Des milliers de véhicules disponibles près de chez vous.</p>
              <Link to="/" className="mc-btn mc-btn--primary mc-btn--sm mc-btn--block">
                Rechercher
              </Link>
            </div>
          </aside>

          {/* ════ Contenu principal ════ */}
          <section aria-labelledby="guest-resa-title">
            <div className="mc-guest-head">
              <div>
                <h1 className="mc-account-title" id="guest-resa-title">
                  Mes réservations
                </h1>
                {!isLoading && (
                  <p className="mc-guest-subtitle" aria-live="polite">
                    <strong>{ReservationData.length}</strong> réservation
                    {ReservationData.length !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
              <Link to="/" className="mc-btn mc-btn--secondary mc-btn--sm mc-guest-new">
                <i className="bi bi-plus-lg" aria-hidden="true"></i>
                Nouvelle réservation
              </Link>
            </div>

            {/* Onglets de filtre */}
            <div className="mc-guest-tabs" role="group" aria-label="Filtrer par statut">
              {STATUS_TABS.map((tab) => {
                const count = countByTab(tab.key);
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveTab(tab.key)}
                    aria-pressed={isActive}
                    className={`mc-guest-tab${isActive ? ' is-active' : ''}`}
                  >
                    {tab.label}
                    {count > 0 && <span className="mc-guest-tab-count">{count}</span>}
                  </button>
                );
              })}
            </div>

            {/* Liste */}
            {isLoading ? (
              <div className="mc-guest-list" aria-busy="true" aria-label="Chargement">
                <div className="mc-guest-skeleton"></div>
                <div className="mc-guest-skeleton"></div>
                <div className="mc-guest-skeleton"></div>
              </div>
            ) : filtered.length === 0 ? (
              <div className="mc-guest-empty">
                <span className="mc-guest-empty-icon" aria-hidden="true">
                  <i className="bi bi-calendar-x"></i>
                </span>
                <p>
                  {activeTab === 'all'
                    ? "Vous n'avez pas encore de réservation."
                    : 'Aucune réservation dans cette catégorie.'}
                </p>
                {activeTab === 'all' && (
                  <Link className="mc-btn mc-btn--primary" to="/">
                    <i className="bi bi-search" aria-hidden="true"></i>
                    Rechercher une voiture
                  </Link>
                )}
              </div>
            ) : (
              <div className="mc-guest-list">
                {filtered.map((r) => (
                  <GuestHistoricVoyageItem key={r.reservationId} data={r} />
                ))}
              </div>
            )}
          </section>
        </div>
      </div>

      {/* Nav mobile bas de page */}
      <div className="mc-guest-bottombar">
        <GuestNavlist />
      </div>
    </main>
  );
}

export default GuestReservationPage;
